import { useState, useCallback } from "react";
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";

export interface DragItem {
  id: string | number;
  position?: number;
}

export interface UseDragAndDropOptions<T extends DragItem> {
  items: T[];
  onReorder: (reorderedItems: T[]) => void;
  keyExtractor?: (item: T) => string | number;
}

export interface DragAndDropHandlers<T extends DragItem = DragItem> {
  draggedIndex: number | null;
  dragOverIndex: number | null;
  isDragging: boolean;
  isDraggedItem: (index: number) => boolean;
  isDragOverItem: (index: number) => boolean;
  sensors: any;
  handleDndDragStart: (event: DragStartEvent) => void;
  handleDndDragEnd: (event: DragEndEvent) => void;
  items: T[];
  keyExtractor: (item: T) => string | number;
  activeItem: T | null;
}

export function useDragAndDrop<T extends DragItem>({
  items,
  onReorder,
  keyExtractor = (item) => item.id,
}: UseDragAndDropOptions<T>): DragAndDropHandlers<T> {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const activeItem: T | null = activeId
    ? items.find(
        (item) => keyExtractor(item).toString() === activeId.toString()
      ) || null
    : null;

  const handleDndDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveId(event.active.id);
      const index = items.findIndex(
        (item) => keyExtractor(item).toString() === event.active.id.toString()
      );
      setDraggedIndex(index);
    },
    [items, keyExtractor]
  );

  const handleDndDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (active.id !== over?.id && over) {
        const oldIndex = items.findIndex(
          (item) => keyExtractor(item).toString() === active.id.toString()
        );
        const newIndex = items.findIndex(
          (item) => keyExtractor(item).toString() === over.id.toString()
        );

        const reorderedItems = arrayMove(items, oldIndex, newIndex);

        const itemsWithUpdatedPositions = reorderedItems.map((item, index) => ({
          ...item,
          ...(item.position !== undefined && { position: index + 1 }),
        })) as T[];

        onReorder(itemsWithUpdatedPositions);
      }

      setActiveId(null);
      setDraggedIndex(null);
    },
    [items, onReorder, keyExtractor]
  );

  // Utility functions
  const isDragging = activeId !== null;
  const isDraggedItem = useCallback(
    (index: number) => draggedIndex === index,
    [draggedIndex]
  );
  const isDragOverItem = useCallback(() => false, []);

  return {
    draggedIndex,
    dragOverIndex: null,
    isDragging,
    isDraggedItem,
    isDragOverItem,
    sensors,
    handleDndDragStart,
    handleDndDragEnd,
    items,
    keyExtractor,
    activeItem,
  };
}
